import {
  AwardIcon,
  DollarSignIcon,
  PhoneIcon,
  SendIcon,
  UserCheckIcon,
  type LucideIcon,
} from "lucide-react";

import {
  INTERVIEWS_GRADIENT,
  PLACEMENTS_GRADIENT,
  REVENUE_GRADIENT,
  SCREENS_GRADIENT,
  SUBMITTALS_GRADIENT,
} from "@/lib/constants";
import { formatting } from "@/lib/formatting";

import type { HexGradient, LeaderboardUser } from "@/types";

// eslint-disable-next-line @typescript-eslint/consistent-type-definitions
type BadgeMetric = {
  key: keyof LeaderboardUser;
  // Hover-card title for the category, e.g. "Most Revenue".
  label: string;
  icon: LucideIcon;
  gradient: HexGradient;
  format: (value: number) => string;
};

const formatCount = (value: number): string => value.toLocaleString("en-US");

// The five categories that earn a leader badge, in the order shown beside a
// staffer's name. Single source of truth for the badges and the top-3 hover cards.
const BADGE_METRICS: BadgeMetric[] = [
  {
    key: "revenue",
    label: "Most Revenue",
    icon: DollarSignIcon,
    gradient: REVENUE_GRADIENT,
    format: formatting.currency,
  },
  {
    key: "placements",
    label: "Most Placements",
    icon: AwardIcon,
    gradient: PLACEMENTS_GRADIENT,
    format: formatCount,
  },
  {
    key: "screens",
    label: "Most Screens",
    icon: PhoneIcon,
    gradient: SCREENS_GRADIENT,
    format: formatCount,
  },
  {
    key: "submittals",
    label: "Most Submits",
    icon: SendIcon,
    gradient: SUBMITTALS_GRADIENT,
    format: formatCount,
  },
  {
    key: "interviews",
    label: "Most Interviews",
    icon: UserCheckIcon,
    gradient: INTERVIEWS_GRADIENT,
    format: formatCount,
  },
];

// A user leads a metric when they hold (or tie) the highest positive value.
const isLeader = (
  users: LeaderboardUser[],
  userId: number,
  key: keyof LeaderboardUser,
): boolean => {
  const maxValue = Math.max(...users.map((u) => u[key] as number), 0);
  const userValue = users.find((u) => u.userId === userId)?.[key] as
    | number
    | undefined;
  return maxValue > 0 && userValue === maxValue;
};

// A user has meaningful activity when any pipeline-value metric is positive —
// gates who shows on the leaderboard beyond the active-staffer pool.
const hasActivity = (user: LeaderboardUser): boolean =>
  user.submittals > 0 ||
  user.interviews > 0 ||
  user.offers > 0 ||
  user.placements > 0 ||
  user.revenue > 0;

// Top n staffers for a metric, highest first, dropping zero values.
const topN = (
  users: LeaderboardUser[],
  key: keyof LeaderboardUser,
  n: number,
): LeaderboardUser[] =>
  [...users]
    .filter((u) => (u[key] as number) > 0)
    .sort((a, b) => (b[key] as number) - (a[key] as number))
    .slice(0, n);

export const leaderboardMetrics = {
  BADGE_METRICS,
  hasActivity,
  isLeader,
  topN,
};
